import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { PublisherRepresentativeCreateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { EmailVerificationIndicator } from "../../../common/controls/EmailVerificationIndicator";
import { InputPhone } from "../../../common/controls";
import { InputBoolean } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type PublisherRepresentativeCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  nameSelectedValue?: { displayId: string; id: string } | null;
  onNameChange?: (value: string) => void;
  name: string;
  nameError?: string;
  roleSelectedValue?: { displayId: string; id: string } | null;
  onRoleChange?: (value: string) => void;
  role: string;
  roleError?: string;
  emailSelectedValue?: { displayId: string; id: string } | null;
  onEmailChange?: (value: string) => void;
  email: string;
  emailError?: string;
  phoneSelectedValue?: { displayId: string; id: string } | null;
  onPhoneChange?: (value: string) => void;
  phone: string;
  phoneError?: string;
  isSignerSelectedValue?: { displayId: string; id: string } | null;
  onIsSignerChange?: (value: boolean) => void;
  isSigner: boolean;
  isSignerError?: string;
  publisherRepresentativeCreateStore: PublisherRepresentativeCreateStore;
};

export const PublisherRepresentativeCreateForm: React.FC<PublisherRepresentativeCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      name = "",
      nameError,
      onNameChange = () => {},
      role = "",
      roleError,
      onRoleChange = () => {},
      email = "",
      emailError,
      onEmailChange = () => {},
      phone = "",
      phoneError,
      onPhoneChange = () => {},
      isSigner = false,
      isSignerError,
      onIsSignerChange = () => {},
      publisherRepresentativeCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="PublisherRepresentative"
              mode="create"
              values={{}}
              options={{}}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "name":
                    onNameChange?.(valor);
                    break;
                  case "role":
                    onRoleChange?.(valor);
                    break;
                  case "email":
                    onEmailChange?.(valor);
                    break;
                  case "phone":
                    onPhoneChange?.(valor);
                    break;
                  case "isSigner":
                    onIsSignerChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={publisherRepresentativeCreateStore.getCardErrors(
                "null"
              )}
              title={langStore.groupPublisherRepresentativeLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={name}
                  error={Boolean(nameError)}
                  warning={
                    !nameError &&
                    Boolean(publisherRepresentativeCreateStore.warnings["name"])
                  }
                  helperText={
                    nameError ||
                    publisherRepresentativeCreateStore.warnings["name"]?.message
                  }
                  label={langStore.PublisherRepresentativeNameLabel}
                  onChange={(value: string) => onNameChange(value)}
                  description={langStore.PublisherRepresentativeNameDescription}
                />

                <InputString
                  value={role}
                  error={Boolean(roleError)}
                  warning={
                    !roleError &&
                    Boolean(publisherRepresentativeCreateStore.warnings["role"])
                  }
                  helperText={
                    roleError ||
                    publisherRepresentativeCreateStore.warnings["role"]?.message
                  }
                  label={langStore.PublisherRepresentativeRoleLabel}
                  onChange={(value: string) => onRoleChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.PublisherRepresentativeRoleDescription}
                />

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <InputString
                    value={email}
                    error={Boolean(emailError)}
                    warning={
                      !emailError &&
                      Boolean(
                        publisherRepresentativeCreateStore.warnings["email"]
                      )
                    }
                    helperText={
                      emailError ||
                      publisherRepresentativeCreateStore.warnings["email"]
                        ?.message
                    }
                    label={langStore.PublisherRepresentativeEmailLabel}
                    onChange={(value: string) => onEmailChange(value)}
                    description={
                      langStore.PublisherRepresentativeEmailDescription
                    }
                  />
                  <EmailVerificationIndicator
                    email={email}
                    entityName="PublisherRepresentative"
                  />
                </Box>

                <InputPhone
                  value={phone}
                  error={Boolean(phoneError)}
                  helperText={
                    phoneError ||
                    publisherRepresentativeCreateStore.warnings["phone"]
                      ?.message
                  }
                  label={langStore.PublisherRepresentativePhoneLabel}
                  onChange={(value: string) => onPhoneChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={
                    langStore.PublisherRepresentativePhoneDescription
                  }
                />
                <InputBoolean
                  value={isSigner}
                  error={Boolean(isSignerError)}
                  helperText={
                    isSignerError ||
                    publisherRepresentativeCreateStore.warnings["isSigner"]
                      ?.message
                  }
                  label={langStore.PublisherRepresentativeIsSignerLabel}
                  description={
                    langStore.PublisherRepresentativeIsSignerDescription
                  }
                  onChange={(value: boolean) => onIsSignerChange(value)}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
